import type { PolicyTestResult, RuleExecution, TestResult } from '@klarna/cbrms-policy-rules-manager-client-ts'
import type { AssertableField, AssertionType, Decision, RuleTest, RuleTestResult } from '../types'

export interface DecisionOption {
  label: string
  value: Decision | 'SKIPPED'
}

/** Possible options for the rule test expected result */
export const decisionOptions: DecisionOption[] = [
  { label: 'Rule is skipped', value: 'SKIPPED' },
  { label: 'accepted', value: 'ACCEPTED' },
  { label: 'rejected', value: 'REJECTED' },
]

interface ValueOption {
  value: string
  label: string
}

export interface ExpectedResultOption {
  /** Displayed in select dropdowns */
  label: string
  /** Displayed in granular computed test name */
  template: string
  name: AssertableField
  assertionType: AssertionType
  defaultValue?: string
  valueOptions?: ValueOption[]
}

export const rejectedResultOptions: ExpectedResultOption[] = [
  {
    label: 'description equals',
    template: 'desc = {value}',
    name: 'description',
    assertionType: 'Equals',
  },
  {
    label: 'authentication exists',
    template: 'auth {valueLabel}',
    name: 'authentication',
    assertionType: 'Exists',
    defaultValue: 'true',
    valueOptions: [
      { value: 'true', label: 'Is set' },
      { value: 'false', label: 'Not set' },
    ],
  },
  {
    label: 'code equals',
    template: 'code = {value}',
    name: 'code',
    assertionType: 'Equals',
    defaultValue: 'HIGH_RISK',
  },
  {
    label: 'category equals',
    template: 'category = {value}',
    name: 'category',
    assertionType: 'Equals',
    defaultValue: 'HIGH_RISK',
  },
]

export const acceptedResultOptions: ExpectedResultOption[] = [
  {
    label: 'description equals',
    template: 'desc = {value}',
    name: 'description',
    assertionType: 'Equals',
  },
  {
    label: 'authentication exists',
    template: 'auth {valueLabel}',
    name: 'authentication',
    assertionType: 'Exists',
    defaultValue: 'true',
    valueOptions: [
      { value: 'true', label: 'Is set' },
      { value: 'false', label: 'Not set' },
    ],
  },
  {
    label: 'review equals',
    template: 'review = {value}',
    name: 'review',
    assertionType: 'Equals',
    defaultValue: 'true',
    valueOptions: [
      { value: 'true', label: 'True' },
      { value: 'false', label: 'False' },
    ],
  },
  {
    label: 'fallback decision equals',
    template: 'fallback = {value}',
    name: 'fallback_decision',
    assertionType: 'Equals',
    defaultValue: 'ACCEPT',
    valueOptions: [
      { value: 'ACCEPT', label: 'ACCEPT' },
      { value: 'REJECT', label: 'REJECT' },
    ],
  },
  {
    label: 'pending required equals',
    template: 'pending_req = {value}',
    name: 'pending_required',
    assertionType: 'Equals',
    defaultValue: 'true',
    valueOptions: [
      { value: 'true', label: 'True' },
      { value: 'false', label: 'False' },
    ],
  },
]

export type ResultOptionKey = Decision | 'SKIPPED'

/** The extra detailed assertion options we get for particular decision results in rule tests */
export const resultOptions: {
  [key in Decision | 'SKIPPED']: ExpectedResultOption[]
} = {
  ACCEPTED: acceptedResultOptions,
  REJECTED: rejectedResultOptions,
  SKIPPED: [],
}

export type AssertionResult = AssertionSuccess | AssertionFailure

export interface AssertionFailure {
  kind: 'fail'
  message: string
}

export interface AssertionSuccess {
  kind: 'success'
}

export const getTestResultAsString = (testResult: TestResult) => {
  if (testResult.error) {
    const { error } = testResult
    return `${error.name}("${error.message}")`
  }

  // This is only relevant for Rule Tests
  // TODO: rethink how to split up this func as this is poor re-use, or get backend to give us an
  // explicit "SKIPPED" result because this feels akward
  if (typeof testResult.result === 'undefined') {
    return 'SKIPPED'
  }

  return JSON.stringify(testResult.result!, null, 2)
}

export const getPolicyTestRuleResultAsString = (result: RuleExecution) => {
  return JSON.stringify(result.decision, null, 2)
}

export const getPolicyTestResultAsString = (testResult: PolicyTestResult) => {
  return JSON.stringify(testResult.decision, null, 2)
}

export const getDecisionAsString = (item: TestResult | RuleTest) => {
  if (item.result) {
    const result = item.result!

    if ((result as RuleTestResult['result'])!.status) {
      return (result as RuleTestResult['result'])!.status.toUpperCase()
    }

    if ((result as RuleTest['result'])!.decision) {
      return (result as RuleTest['result'])!.decision.toUpperCase()
    }
  }

  return 'SKIPPED'
}
